/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';
import type { ReactNode } from 'react';

import { jsx, cssMap, cx } from '@compiled/react';

import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import { Box } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import { useToolbarUI } from '../hooks/ui-context';

import { ToolbarButton } from './ToolbarButton';
import { useToolbarDropdownMenu } from './ToolbarDropdownMenuContext';

const styles = cssMap({
	sectionMargin: {
		marginBlock: token('space.025'),
	},

	firstSectionSeparator: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'[data-section]:nth-of-type(1)': {
			// Remove separator from first section
			borderBlockStart: 'unset',
		},
	},
	scrollContainer: {
		maxHeight: '320px',
		overflowY: 'auto',
	},
});

type ToolbarDropdownMenuProps = {
	children?: ReactNode;
	/**
	 * Enforces a max height of 320px for menus - when menu is larger a scroll is introduced
	 */
	enableMaxHeight?: boolean;
	/**
	 * Whether to add margin around sections to align with 4px block padding existing in current editor dropdown
	 */
	hasSectionMargin?: boolean;
	iconBefore: React.ReactNode;
	id?: string;
	isDisabled?: boolean;
	label?: string;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>, isOpen: boolean) => void;
	/**
	 * Renders the dropdown menu inline (into its parent) rather than into a portal. This opts the
	 * menu out of the popup focus-trap's return-focus-to-trigger behaviour on close, which lets
	 * focus return to the editor after selecting an item. Opt-in per menu to avoid changing the
	 * rendering of every toolbar dropdown.
	 */
	shouldRenderToParent?: boolean;
	testId?: string;
	tooltipComponent?: React.ReactNode;
};

const ToolbarDropdownMenuContent = ({
	iconBefore,
	tooltipComponent,
	children,
	isDisabled,
	id,
	testId,
	label,
	onClick,
	shouldRenderToParent,
}: ToolbarDropdownMenuProps) => {
	const { onDropdownOpenChanged } = useToolbarUI();
	const menuContext = useToolbarDropdownMenu();

	const handleOpenChange = useCallback(
		(args: OnOpenChangeArgs) => {
			onDropdownOpenChanged(args);
			if (!args.isOpen) {
				menuContext?.closeMenu(args.event);
			}
		},
		[menuContext, onDropdownOpenChanged],
	);

	const handleClick = useCallback(
		(e: React.MouseEvent) => {
			if (!menuContext?.isOpen) {
				menuContext?.openMenu(e);
			} else {
				menuContext?.closeMenu(e);
			}
		},
		[menuContext],
	);

	return (
		<DropdownMenu<HTMLButtonElement>
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			trigger={(triggerProps) => {
				const toolbarButton = (
					<ToolbarButton
						ref={triggerProps.triggerRef}
						isSelected={triggerProps.isSelected}
						aria-expanded={triggerProps['aria-expanded']}
						aria-haspopup={triggerProps['aria-haspopup']}
						aria-controls={triggerProps['aria-controls']}
						onBlur={triggerProps.onBlur}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onClick={(e) => {
							onClick && onClick(e, !menuContext?.isOpen);
							handleClick(e);
							triggerProps.onClick && triggerProps.onClick(e);
						}}
						onFocus={triggerProps.onFocus}
						id={
							expValEquals('platform_editor_renderer_toolbar_updates', 'isEnabled', true)
								? id
								: undefined
						}
						testId={testId}
						iconBefore={iconBefore}
						isDisabled={isDisabled}
						label={label}
					/>
				);

				if (tooltipComponent) {
					return React.cloneElement(tooltipComponent as React.ReactElement, {
						children: toolbarButton,
					});
				}

				return toolbarButton;
			}}
			onOpenChange={handleOpenChange}
			isOpen={menuContext?.isOpen}
			shouldRenderToParent={shouldRenderToParent ? true : undefined}
		>
			{children}
		</DropdownMenu>
	);
};

export const ToolbarDropdownMenu = ({
	iconBefore,
	children,
	isDisabled,
	id,
	testId,
	label,
	hasSectionMargin = true,
	enableMaxHeight = false,
	onClick,
	tooltipComponent,
	shouldRenderToParent,
}: ToolbarDropdownMenuProps): JSX.Element => {
	return (
		<ToolbarDropdownMenuContent
			iconBefore={iconBefore}
			isDisabled={isDisabled}
			id={
				expValEquals('platform_editor_renderer_toolbar_updates', 'isEnabled', true) ? id : undefined
			}
			testId={testId}
			label={label}
			onClick={onClick}
			tooltipComponent={tooltipComponent}
			shouldRenderToParent={shouldRenderToParent}
		>
			<Box
				xcss={cx(
					hasSectionMargin && styles.sectionMargin,
					enableMaxHeight && styles.scrollContainer,
					styles.firstSectionSeparator,
				)}
				data-toolbar-component="menu"
			>
				{children}
			</Box>
		</ToolbarDropdownMenuContent>
	);
};
